/*
 *  @providesModule Preferences
 *
 */

'use client';

import {setupFeatureOverrides} from './overrides';
import getGameId from '@/utils/getGameId';
import {GAME_SETTINGS_LS_NAME} from '@/utils/constant';
import {FTUEConstants} from './FTUEConstants';
import {clearLocalStorage} from '@/utils/clearLocalStorage';

export type APIClientSetting = {
  key: string;
  value: string;
};

export type ClientSetting = Record<string, any>;

type WatchSubscription = {keys: string[]; callback: (() => void) | null};
const subscriptions: WatchSubscription[] = [];

const notifyWatchers = (changedKeys: string[]) => {
  if (!changedKeys.length) return;
  subscriptions.forEach((sub) => {
    if (sub.callback && sub.keys.some((k) => changedKeys.includes(k))) sub.callback();
  });
};

const getSettingsKey = () => {
  const id = getGameId();
  return `${GAME_SETTINGS_LS_NAME}${id}`;
};

// Cache the parsed settings blob per game key. getClientProperty is called on hot
// render paths (e.g. DepositSlate reads the slate variant every render) and the blob
// is ~700 keys, so JSON.parse-ing localStorage on every read is measurable churn.
// Invalidated by the write paths (set / setClientProperty) below; a game switch keys
// on getSettingsKey() so a stale cache from another game can't leak.
let clientPropsCache: {key: string; props: Record<string, any>} | null = null;
const invalidateClientPropsCache = () => {
  clientPropsCache = null;
};

const getClientProperties = () => {
  if (typeof window === 'undefined') {
    console.warn('Fetching properties server-side, ignore');
    return {};
  }

  const key = getSettingsKey();
  if (clientPropsCache && clientPropsCache.key === key) {
    return clientPropsCache.props;
  }

  try {
    const persisted = JSON.parse(window.localStorage.getItem(key) ?? '{}');
    const props = persisted.theme.settings ?? {};
    clientPropsCache = {key, props};
    return props;
  } catch (e) {
    console.warn('no settings detected');
    return {};
  }
};

const getClientProperty = (key: string) => {
  const property = getClientProperties()[key];
  if (property !== null && property !== undefined) {
    return property;
  }

  // If not found in client properties, check window.__CLIENT_PROPERTIES__ as fallback
  try {
    const initData =
      typeof window !== 'undefined' && window.__CLIENT_PROPERTIES__
        ? typeof window.__CLIENT_PROPERTIES__ === 'string'
          ? JSON.parse(window.__CLIENT_PROPERTIES__)
          : window.__CLIENT_PROPERTIES__
        : null;

    if (initData && initData[key] !== undefined) {
      return initData[key];
    }
  } catch (e) {
    console.warn('Failed to parse window.__CLIENT_PROPERTIES__ for key:', key, e);
  }

  return null;
};

const setClientProperty = (key: string, value: boolean | string | number) => {
  try {
    const persisted = JSON.parse(window.localStorage.getItem(getSettingsKey()) ?? '{}');
    persisted.theme.settings[key] = value;
    window.localStorage.setItem(getSettingsKey(), JSON.stringify(persisted));
    invalidateClientPropsCache();
    notifyWatchers([key]);
  } catch (e) {
    console.warn('no settings detected');
    return {};
  }
};

const getBoolClientProperty = (key: string, defaultValue = false) => {
  const property = getClientProperty(key);
  if (property === null || (property === undefined && defaultValue !== undefined)) {
    return defaultValue;
  }
  return !!JSON.parse(String(property).toLowerCase());
};

const getIntClientProperty = (key: string, defaultValue = 0) => {
  const property = getClientProperty(key);
  // Return the parsed integer, not the raw string. Callers (e.g. getVariantConfig's
  // strict `v.id === variant`) compare against numbers, so returning "3" instead of 3
  // silently missed and fell back to defaults — the deposit-slate variant rendered
  // blue/pink (variant 1 default) instead of the configured green/gold (3). Native's
  // Preferences returns an int; this matches it.
  const parsed = parseInt(property, 10);
  return property != null && Number.isInteger(parsed) ? parsed : (defaultValue ?? 0);
};

const set = (settings: Record<string, any>) => {
  try {
    const persisted = JSON.parse(window.localStorage.getItem(getSettingsKey()) ?? '{}');
    persisted.theme.settings = {
      ...persisted.theme.settings,
      ...settings,
    };
    window.localStorage.setItem(getSettingsKey(), JSON.stringify(persisted));
    invalidateClientPropsCache();
    notifyWatchers(Object.keys(settings));
  } catch (e) {
    console.warn('no settings detected');
    return {};
  }
};

const setAll = (settings: ClientSetting) => {
  if (settings) {
    const props = setupFeatureOverrides(settings);
    set(props);
  }
};

const logFTUESettings = () => {
  console.log(
    'FTUE: settings',
    Object.fromEntries(
      Object.values(FTUEConstants).map((value) => [value, getClientProperty(value)])
    )
  );
};

const enableFullFTUE = () => {
  const ftueSettings = Object.fromEntries(
    Object.values(FTUEConstants).map((value) => [value, false])
  );

  set(ftueSettings);
  setClientProperty(FTUEConstants.kShouldWalkthroughVersus, true);

  logFTUESettings();
};

const enableSecondFTUE = () => {
  const ftueSettings = Object.fromEntries(
    Object.values(FTUEConstants).map((value) => [value, false])
  );

  set(ftueSettings);
  setClientProperty(FTUEConstants.kCreatedProfileTutorialKey, true);
  setClientProperty(FTUEConstants.kJoinedFirstFTUETournamentKey, true);
  setClientProperty(FTUEConstants.kVersusTutorialOneKey, true);
  setClientProperty(FTUEConstants.kTabTutorialKey, true);
  setClientProperty(FTUEConstants.kPLOWCompletedTutorialOneKey, true);
  setClientProperty(FTUEConstants.kShouldWalkthroughVersus, true);

  logFTUESettings();
};

const disableFTUE = () => {
  const excludedKeys = [FTUEConstants.kLiveBracketTutorial];
  const liveBracketValue = getClientProperty(FTUEConstants.kLiveBracketTutorial);

  const ftueSettings = Object.fromEntries(
    Object.values(FTUEConstants).map((value) => [
      value,
      excludedKeys.includes(value)
        ? liveBracketValue // Preserve its current state
        : true,
    ])
  );

  set(ftueSettings);
  setClientProperty(FTUEConstants.kShouldWalkthroughVersus, false);

  logFTUESettings();
};

const watchKeys = (keys: string | string[], callback: () => void): number => {
  const keyList = typeof keys === 'string' ? [keys] : keys;
  const watchId = subscriptions.length;
  subscriptions.push({keys: keyList, callback});
  return watchId;
};

const clearWatch = (watchId: number) => {
  if (watchId >= 0 && watchId < subscriptions.length) {
    subscriptions[watchId] = {keys: [], callback: null};
  }
};

const Preferences = {
  getClientProperty,
  setClientProperty,
  getBoolClientProperty,
  getIntClientProperty,
  set,
  get: getClientProperty,
  setAll,
  watchKeys,
  clearWatch,
  clearAllStorage: clearLocalStorage,
  enableFullFTUE,
  enableSecondFTUE,
  disableFTUE,
};

export default Preferences;
